import { graphql } from '@/gql'

export const WORKER_VIEW_FRAGMENT = graphql(/* GraphQL */ `
  fragment WorkerViewFragment on Worker {
    id
    name
    email
    phone
    address {
      city
      state
      country {
        isoCode
        name
      }
    }
    initials
    avatar
    avatarPath @client
    jobTitle
    market {
      code
      name
    }
    currency
    dayRate
    skills {
      id
      name
    }
    totalPaid(account: $companyId)
    isCurrentlyHired(account: $companyId)
    viewerCanContact(account: $companyId)
    viewerCanOfferJob(account: $companyId)
    engagedViaSupplierName(account: $companyId)
    hires(companies: [$companyId], isAccepted: true) {
      paginatorInfo {
        total
      }
    }
    pendingHires: hires(companies: [$companyId], isAccepted: false) {
      paginatorInfo {
        total
      }
    }
    activeHires: hires(companies: [$companyId], isAccepted: true, status: ACTIVE) {
      paginatorInfo {
        total
      }
      data {
        id
        createdAt
        job {
          id
          name
        }
        company {
          name
        }
        paymentRequests(
          workerStatuses: [REQUESTED, APPROVED, PROCESSING, COMPLETED, REJECTED, CANCELLED]
        ) {
          data {
            id
            number
          }
        }
      }
    }
    completedHires: hires(companies: [$companyId], isAccepted: true, status: ENDED) {
      data {
        id
        createdAt
        job {
          id
          name
        }
        company {
          name
        }
        paymentRequests(
          workerStatuses: [REQUESTED, APPROVED, PROCESSING, COMPLETED, REJECTED, CANCELLED]
        ) {
          data {
            id
            number
          }
        }
      }
    }
    primaryBusinessEntity {
      name
      type
    }
    profile {
      url
      links {
        title
        url
      }
    }
    hiresWithAttribution {
      recruiter {
        name
      }
      fees {
        ...FeeFragment
      }
      recruiterManagesWorkers
      recruiterOwnershipStartDate
      recruiterOwnershipDays
      recruiterOwnershipDaysLeft
    }
    insurances {
      type
      title
      hasInsurance
      policyLimit
      updatedAt
      files {
        id
        name
        title
        url
      }
    }
  }
`)

export const TRUSTED_CONTACT_VIEW_FRAGMENT = graphql(/* GraphQL */ `
  fragment TrustedContactViewFragment on TrustedContact {
    id
    company {
      name
    }
    externalIdentifier
    onboardingStatus
    requiredCustomFieldsComplete
    onboardingDocuments {
      id
      file {
        id
        name
        title
      }
      freelancerAcceptedAt
      isAccepted
    }
    links
    attachments {
      name
      url
    }
    skills {
      id
      name
    }
    status
    statusUpdatedAt
    notes {
      paginatorInfo {
        total
      }
    }
    viewerCanApprove(account: $companyId)
    viewerCanUpdate(account: $companyId)
    viewerCanDelete(account: $companyId)
    viewerCanReInvite(account: $companyId)
    createdAt
    status
    originChannel
    invitedAt
    invitedByUser {
      name
    }
    approvedByUser {
      name
    }
    worker {
      ...WorkerViewFragment
    }
    customFieldValues {
      ...CustomFieldValuesView
    }
  }
`)

export const CONVERSATIONS_VIEW_FRAGMENT = graphql(/* GraphQL */ `
  fragment ConversationsViewFragment on Conversation {
    id
    subject
    url
    createdAt
    latestMessage {
      author {
        name
      }
    }
  }
`)

export const TRUSTED_CONTACTS_NOTES_FRAGMENT = graphql(/* GraphQL */ `
  fragment TrustedContactNotesFragment on TrustedContact {
    id
    notes(first: 100) {
      data {
        ...NoteViewFragment
      }
      paginatorInfo {
        total
      }
    }
  }
`)

export const PARTNER_WORKER_VIEW_FRAGMENT = graphql(/* GraphQL */ `
  fragment PartnerWorkerViewFragment on Worker {
    id
    name
    email
    phone
    address {
      city
      state
      country {
        isoCode
        name
      }
    }
    initials
    avatar
    avatarPath @client
    jobTitle
    market {
      code
      name
    }
    currency
    dayRate
    skills {
      id
      name
    }
    primaryBusinessEntity {
      name
    }
    profile {
      url
      links {
        title
        url
      }
    }
    # Get hires for partner context (partners see employment data through hires)
    hires(first: 10, isAccepted: true) {
      data {
        id
        number
        status
        startDate
        endDate
        offeredAt
        currency
        job {
          id
          number
          name
          externalIdentifier
          owners {
            id
            name
          }
          customFieldValues {
            ...CustomFieldValuesView
          }
        }
        company {
          id
          name
        }
        activeContract {
          id
          jobName
          startDate
          endDate
          rate
          rateType
          currency
          locationPreference
          workerAcceptedAt
          location {
            formattedAddress
          }
        }
        employment {
          id
          employmentCosts {
            data {
              id
              category
              type
              frequency
              amount
              createdAt
            }
          }
        }
      }
    }
  }
`)
